import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/**
 * SEO hook — sets document <title>, description, canonical, hreflang,
 * Open Graph + Twitter tags on every route change.
 *
 * Pass { title, description, image? } — the rest is handled automatically
 * (canonical from window.location, hreflang/x-default, site name, lang).
 *
 * Also bumps `<html lang>` to the active UI language.
 */
const SITE_NAME = 'Casanova Salon Barbier';
const DEFAULT_OG_IMAGE =
  'https://customer-assets.emergentagent.com/job_italian-grooming/artifacts/xeejzpdg_FullLockup-Orange.png';

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs.create || {}).forEach(([k, v]) =>
      el.setAttribute(k, v)
    );
    document.head.appendChild(el);
  }
  Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
};

const upsertLink = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    Object.entries(attrs.create || {}).forEach(([k, v]) =>
      el.setAttribute(k, v)
    );
    document.head.appendChild(el);
  }
  Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
};

const useSEO = ({ title, description, image, breadcrumb }) => {
  const { language } = useLanguage();
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title
      ? `${title} · ${SITE_NAME}`
      : `${SITE_NAME} · Petite-Italie, Montréal`;
    document.title = fullTitle;

    const url =
      typeof window !== 'undefined'
        ? `${window.location.origin}${pathname}`
        : '';
    const ogImage = image || DEFAULT_OG_IMAGE;

    // Standard description
    upsertMeta('meta[name="description"]', {
      create: { name: 'description' },
      set: { content: description || '' },
    });

    // Canonical
    upsertLink('link[rel="canonical"]', {
      create: { rel: 'canonical' },
      set: { href: url },
    });

    // hreflang — FR & EN point to the same URL (language persists in
    // localStorage, no URL-level split — acceptable per Google guidelines
    // when the UI language is user-selectable and persisted).
    upsertLink('link[rel="alternate"][hreflang="fr"]', {
      create: { rel: 'alternate', hreflang: 'fr' },
      set: { href: url },
    });
    upsertLink('link[rel="alternate"][hreflang="en"]', {
      create: { rel: 'alternate', hreflang: 'en' },
      set: { href: url },
    });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', {
      create: { rel: 'alternate', hreflang: 'x-default' },
      set: { href: url },
    });

    // Open Graph
    upsertMeta('meta[property="og:title"]', {
      create: { property: 'og:title' },
      set: { content: fullTitle },
    });
    upsertMeta('meta[property="og:description"]', {
      create: { property: 'og:description' },
      set: { content: description || '' },
    });
    upsertMeta('meta[property="og:type"]', {
      create: { property: 'og:type' },
      set: { content: 'website' },
    });
    upsertMeta('meta[property="og:url"]', {
      create: { property: 'og:url' },
      set: { content: url },
    });
    upsertMeta('meta[property="og:site_name"]', {
      create: { property: 'og:site_name' },
      set: { content: SITE_NAME },
    });
    upsertMeta('meta[property="og:image"]', {
      create: { property: 'og:image' },
      set: { content: ogImage },
    });
    upsertMeta('meta[property="og:locale"]', {
      create: { property: 'og:locale' },
      set: { content: language === 'fr' ? 'fr_CA' : 'en_CA' },
    });
    upsertMeta('meta[property="og:locale:alternate"]', {
      create: { property: 'og:locale:alternate' },
      set: { content: language === 'fr' ? 'en_CA' : 'fr_CA' },
    });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', {
      create: { name: 'twitter:card' },
      set: { content: 'summary_large_image' },
    });
    upsertMeta('meta[name="twitter:title"]', {
      create: { name: 'twitter:title' },
      set: { content: fullTitle },
    });
    upsertMeta('meta[name="twitter:description"]', {
      create: { name: 'twitter:description' },
      set: { content: description || '' },
    });
    upsertMeta('meta[name="twitter:image"]', {
      create: { name: 'twitter:image' },
      set: { content: ogImage },
    });

    // html lang (language ctx already handles this, but harmless)
    document.documentElement.lang = language;

    // BreadcrumbList JSON-LD — page-scoped (upsert keyed by id)
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const homeName = language === 'fr' ? 'Accueil' : 'Home';
    const trail =
      breadcrumb && breadcrumb.length > 0
        ? [{ name: homeName, path: '/' }, ...breadcrumb]
        : pathname === '/'
        ? [{ name: homeName, path: '/' }]
        : [
            { name: homeName, path: '/' },
            { name: title || pathname.replace('/', ''), path: pathname },
          ];
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: trail.map((item, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: item.name,
        item: `${origin}${item.path}`,
      })),
    };
    let bcEl = document.head.querySelector(
      'script[data-seo-breadcrumb="1"]'
    );
    if (!bcEl) {
      bcEl = document.createElement('script');
      bcEl.type = 'application/ld+json';
      bcEl.setAttribute('data-seo-breadcrumb', '1');
      document.head.appendChild(bcEl);
    }
    bcEl.textContent = JSON.stringify(breadcrumbSchema);
  }, [title, description, image, pathname, language, breadcrumb]);
};

export default useSEO;
